import { initClient } from "@vm0/api-contracts/contracts/trpc-contract";
import type {
  ComputerUseAuthorizationRequestCreateResponse,
  ComputerUseCommandCreateResponse,
  ComputerUseCommandResponse,
  ComputerUseHost,
  ComputerUseReadCommandKind,
  ComputerUseWriteCommandKind,
} from "@vm0/api-contracts/contracts/zero-computer-use";
import type { ComputerUseAnyPluginCallBody } from "@vm0/api-contracts/contracts/zero-computer-use-plugins";
import {
  zeroComputerUseAuthorizationRequestsContract,
  zeroComputerUseCommandContract,
  zeroComputerUseHostsContract,
  zeroComputerUsePluginCommandContract,
  zeroComputerUseWriteCommandContract,
} from "@vm0/api-contracts/contracts/zero-computer-use";
import {
  ApiRequestError,
  getBaseUrl,
  handleError,
} from "../core/client-factory";
import { getActiveToken } from "../config";
import {
  cliClientHeaderApi,
  headersWithCliClientHeaders,
} from "../client-headers";

function normalizeConfiguredUrl(value: string): string {
  return value.startsWith("http") ? value : `https://${value}`;
}

function resolveComputerUseApiBaseUrl(baseUrl: string): string {
  const override = process.env.VM0_API_BACKEND_URL;
  if (override) {
    return normalizeConfiguredUrl(override).replace(/\/$/, "");
  }

  const url = new URL(baseUrl);
  if (url.hostname === "www.vm0.ai" || url.hostname === "app.vm0.ai") {
    url.hostname = "api.vm0.ai";
  }
  return url.toString().replace(/\/$/, "");
}

function buildHeaders(token?: string): Record<string, string> {
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

async function getComputerUseClientConfig() {
  const baseUrl = resolveComputerUseApiBaseUrl(await getBaseUrl());
  const token = await getActiveToken();
  if (!token) {
    throw new ApiRequestError("Not authenticated", "UNAUTHORIZED", 401);
  }
  return {
    baseUrl,
    baseHeaders: buildHeaders(token),
    jsonQuery: false as const,
    api: cliClientHeaderApi,
  };
}

interface ComputerUseCommandParams<
  Kind extends ComputerUseReadCommandKind | ComputerUseWriteCommandKind,
> {
  readonly kind: Kind;
  readonly app?: string;
  readonly snapshotId?: string;
  readonly elementId?: string;
  readonly elementIndex?: number;
  readonly x?: number;
  readonly y?: number;
  readonly button?: "left" | "right" | "middle";
  readonly clickCount?: number;
  readonly direction?: "up" | "down" | "left" | "right";
  readonly pages?: number;
  readonly value?: string;
  readonly text?: string;
  readonly key?: string;
  readonly action?: string;
  readonly timeoutMs?: number;
}

function commandBody<
  Kind extends ComputerUseReadCommandKind | ComputerUseWriteCommandKind,
>(params: ComputerUseCommandParams<Kind>) {
  return {
    kind: params.kind,
    timeoutMs: params.timeoutMs ?? 15_000,
    ...(params.app ? { app: params.app } : {}),
    ...(params.snapshotId ? { snapshotId: params.snapshotId } : {}),
    ...(params.elementId ? { elementId: params.elementId } : {}),
    ...(params.elementIndex !== undefined
      ? { elementIndex: params.elementIndex }
      : {}),
    ...(params.x !== undefined ? { x: params.x } : {}),
    ...(params.y !== undefined ? { y: params.y } : {}),
    ...(params.button ? { button: params.button } : {}),
    ...(params.clickCount !== undefined
      ? { clickCount: params.clickCount }
      : {}),
    ...(params.direction ? { direction: params.direction } : {}),
    ...(params.pages !== undefined ? { pages: params.pages } : {}),
    ...(params.value !== undefined ? { value: params.value } : {}),
    ...(params.text !== undefined ? { text: params.text } : {}),
    ...(params.key ? { key: params.key } : {}),
    ...(params.action ? { action: params.action } : {}),
  };
}

export async function createComputerUseReadCommand(
  params: ComputerUseCommandParams<ComputerUseReadCommandKind>,
): Promise<ComputerUseCommandCreateResponse> {
  const config = await getComputerUseClientConfig();
  const client = initClient(zeroComputerUseCommandContract, config);
  const result = await client.create({ body: commandBody(params) });

  if (result.status === 200) {
    return result.body;
  }

  handleError(result, "Failed to create computer-use command");
}

export async function createComputerUseWriteCommand(
  params: ComputerUseCommandParams<ComputerUseWriteCommandKind>,
): Promise<ComputerUseCommandCreateResponse> {
  const config = await getComputerUseClientConfig();
  const client = initClient(zeroComputerUseWriteCommandContract, config);
  const result = await client.create({ body: commandBody(params) });

  if (result.status === 200) {
    return result.body;
  }

  handleError(result, "Failed to create computer-use write command");
}

export async function createComputerUsePluginCommand(
  params: ComputerUseAnyPluginCallBody,
): Promise<ComputerUseCommandCreateResponse> {
  const config = await getComputerUseClientConfig();
  const client = initClient(zeroComputerUsePluginCommandContract, config);
  const result = await client.create({ body: params });

  if (result.status === 200) {
    return result.body;
  }

  handleError(result, "Failed to create computer-use plugin command");
}

export async function listComputerUseHosts(): Promise<
  readonly ComputerUseHost[]
> {
  const config = await getComputerUseClientConfig();
  const client = initClient(zeroComputerUseHostsContract, config);
  const result = await client.list({});

  if (result.status === 200) {
    return result.body.hosts;
  }

  handleError(result, "Failed to list computer-use hosts");
}

export async function getComputerUseCommand(
  commandId: string,
): Promise<ComputerUseCommandResponse> {
  const config = await getComputerUseClientConfig();
  const client = initClient(zeroComputerUseCommandContract, config);
  const result = await client.get({ params: { commandId } });

  if (result.status === 200) {
    return result.body;
  }

  handleError(result, "Failed to get computer-use command");
}

export async function createComputerUseAuthorizationRequest(): Promise<ComputerUseAuthorizationRequestCreateResponse> {
  const config = await getComputerUseClientConfig();
  const client = initClient(
    zeroComputerUseAuthorizationRequestsContract,
    config,
  );
  const result = await client.create({ body: {} });

  if (result.status === 200) {
    return result.body;
  }

  handleError(result, "Failed to create computer-use authorization request");
}

/**
 * Download a command screenshot through the authenticated API proxy. The bytes
 * never traverse a public URL; the API authorizes by command ownership and
 * streams the object back. Used when the command result carries an
 * object-storage pointer instead of an inline data URL.
 */
export async function fetchComputerUseScreenshot(
  commandId: string,
): Promise<{ readonly buffer: Buffer; readonly mimeType: string }> {
  const config = await getComputerUseClientConfig();
  const response = await fetch(
    `${config.baseUrl}/api/zero/computer-use/commands/${encodeURIComponent(
      commandId,
    )}/screenshot`,
    { headers: headersWithCliClientHeaders(config.baseHeaders) },
  );

  if (!response.ok) {
    throw new ApiRequestError(
      "Failed to download computer-use screenshot",
      "REQUEST_FAILED",
      response.status,
    );
  }

  const arrayBuffer = await response.arrayBuffer();
  const mimeType =
    response.headers.get("content-type") ?? "application/octet-stream";
  return { buffer: Buffer.from(arrayBuffer), mimeType };
}

export async function fetchComputerUsePluginContent(
  commandId: string,
): Promise<{
  readonly buffer: Buffer;
  readonly mimeType: string;
  readonly fileName: string;
}> {
  const config = await getComputerUseClientConfig();
  const response = await fetch(
    `${config.baseUrl}/api/zero/computer-use/commands/${encodeURIComponent(
      commandId,
    )}/plugin-content`,
    { headers: headersWithCliClientHeaders(config.baseHeaders) },
  );

  if (!response.ok) {
    throw new ApiRequestError(
      "Failed to download computer-use plugin content",
      "REQUEST_FAILED",
      response.status,
    );
  }

  const arrayBuffer = await response.arrayBuffer();
  const mimeType =
    response.headers.get("content-type") ?? "application/octet-stream";
  const disposition = response.headers.get("content-disposition") ?? "";
  const fileName =
    /filename="([^"]+)"/.exec(disposition)?.[1] ?? "plugin-content.bin";
  return { buffer: Buffer.from(arrayBuffer), mimeType, fileName };
}
